import { Injectable } from '@angular/core';

import {
    ActionItem,
    ApiAction,
    ApiActionBundle,
    ApiFragment,
    ExtendedApiAction,
    Mh2Entity,
    ShDynamicDialogService,
    Toast
} from '@stylehaven/components';
import { combineLatest, NEVER, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ActionResultStatus } from '../../entities/actions/action-response.entity';
import { UserAction } from '../../entities/common/user-action.entity';
import { ActionTooltipHelper } from '../../helpers/actions/action-tooltip.helper';
import { ActionsService } from '../../services/api/actions/actions.service';
import { GetGenericApiUserAction } from '../../services/api/actions/generic-api.action';
import { WaitForRecord } from '../../services/api/helpers/wait-for-record-update.helper';
import { SearchService } from '../../services/api/search.service';
import { BaseActionsSandbox } from './actions-base.class';

@Injectable()
export abstract class GenericActionsSandbox extends BaseActionsSandbox<ApiFragment> {
    constructor(
        protected actionsService: ActionsService,
        protected dialogService: ShDynamicDialogService,
        protected tooltipHelper: ActionTooltipHelper,
        protected searchService: SearchService
    ) {
        super();
    }

    public emitActionPerformed(
        action: string,
        target: ApiFragment,
        successful: boolean,
        warrantsRefresh: boolean = true
    ): void {
        if (warrantsRefresh === false) {
            // don't emit for now
            return;
        }
        this.actions$.next({ action, target, successful, warrantsRefresh });
    }

    // #region Generic actions
    protected combineActionStreams(
        baseActions: Observable<ActionItem | null>[],
        apiActions$: Observable<(ActionItem | null)[]>
    ): Observable<ActionItem[]> {
        const baseJoined$: Observable<(ActionItem | null)[]> =
            baseActions.length > 0 ? combineLatest(baseActions) : of([]);

        const allJoined$: Observable<(ActionItem | null)[]> = combineLatest([baseJoined$, apiActions$]).pipe(
            map(([baseActions, apiActions]) => baseActions.concat(apiActions))
        );

        return allJoined$.pipe(map(list => list.filter((x): x is ActionItem => x !== null)));
    }

    protected getApiActions(record: ApiFragment): Observable<(ActionItem | null)[]> {
        const apiActions: Observable<ActionItem | null>[] = record
            .getApiActions()
            .map(action => this.getApiActionItem(record, action));
        return apiActions.length > 0 ? combineLatest(apiActions) : of([]);
    }

    public getApiActionsAsBundle(record: ApiFragment): Observable<ApiActionBundle> {
        return this.getApiActions(record).pipe(
            map(apiActions => apiActions.filter((action): action is ActionItem => !!action)),
            map(apiActions => ({
                label: '',
                apiActions$: of(apiActions)
            }))
        );
    }

    private getApiActionItem(record: ApiFragment, action: ApiAction): Observable<ActionItem | null> {
        return this.actionsService.enrichApiAction(action).pipe(
            map(extendedAction => {
                if (!extendedAction) {
                    return null;
                }

                const userAction: UserAction = GetGenericApiUserAction(record, action);

                return {
                    label: extendedAction.Label,
                    eventListener: this.executeApiAction.bind(this, extendedAction, record),
                    disabled: userAction.isDisabled(),
                    disabledInfoMsg: this.tooltipHelper.getActionTooltip(userAction, [record])
                };
            })
        );
    }

    protected getApiActionBody(action: ApiAction, record: ApiFragment[]): Observable<any> {
        return of(undefined);
    }

    private executeApiAction(action: ExtendedApiAction, record: ApiFragment): void {
        const actionBusyToast: Toast = this.createActionBusyToast(
            'action-toasts.generic',
            Mh2Entity.Generic,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            { action: action.Label }
        );
        this.addToast(actionBusyToast);

        this.getApiActionBody(action, [record])
            .pipe(
                switchMap(body => this.actionsService.executeApiAction(action, record, body)),
                tap(result => {
                    if (result && result?.Status === ActionResultStatus.FAILED) {
                        throw new Error(result.ErrorMessage);
                    }
                }),
                WaitForRecord(record, this.searchService),
                tap(() => this.closeToast(actionBusyToast.id)),
                tap(() => this.actionSuccess(record, action)),
                tap(() => this.emitActionPerformed(action.Rel, record, true)),
                catchError(error => {
                    this.closeToast(actionBusyToast.id);
                    this.emitActionPerformed(action.Rel, record, false);
                    this.actionFailure(error, record, action);
                    return NEVER;
                }),
                take(1),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private actionSuccess(record: ApiFragment, action: ExtendedApiAction): void {
        this.createAndAddActionSuccessToast(
            'action-toasts.generic',
            Mh2Entity.Generic,
            undefined,
            undefined,
            undefined,
            undefined,
            { action: action.Label }
        );
    }

    private actionFailure(error: Error, record: ApiFragment, action: ExtendedApiAction): void {
        if (this.handleCustomErrors(error)) {
            return;
        }

        console.error(`Execution of action ${action.Rel} failed`, error.message);
        this.createAndAddActionErrorToast(
            'action-toasts.generic',
            Mh2Entity.Generic,
            undefined,
            undefined,
            undefined,
            undefined,
            { action: action.Label }
        );
    }
    // #endregion
}
